import {
  UserGroupIcon,
  Cog8ToothIcon,
  RocketLaunchIcon,
  PaperAirplaneIcon,
  UsersIcon,
  UserCircleIcon,
  IdentificationIcon,
  ReceiptPercentIcon,
  PresentationChartBarIcon,
  PuzzlePieceIcon,
  GlobeAmericasIcon,
  CalculatorIcon,
  RectangleStackIcon,
  CreditCardIcon,
  PresentationChartLineIcon,
  CubeIcon,
  MapIcon,
  BookOpenIcon,
  DocumentTextIcon,
  PlusIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  ArrowPathRoundedSquareIcon,
  SwatchIcon,
  ChartBarSquareIcon,
  TruckIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
  QuestionMarkCircleIcon,
  ChartPieIcon,
  QrCodeIcon,
  SparklesIcon,
  TagIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { BookText, Group, Route } from "lucide-react";
import {
  PaperAirplaneIcon as PaperAirplaneSolidIcon,
  PresentationChartBarIcon as PresentationChartBarSolidIcon,
  UserGroupIcon as UserGroupSolidIcon,
  Cog8ToothIcon as Cog8ToothSolidIcon,
  ChartPieIcon as ChartPieSolidIcon,
} from "@heroicons/react/24/solid";
import { NavItem, SUBMENU_ITEM } from "../types";
import {
  FLEET_KEY,
  ORDERS_KEY,
  CREATE_KEY,
  CUSTOMER_KEY,
  QUOTES_KEY,
  SCHEDULES_KEY,
  PACKAGES_KEY,
} from "@src/common/hooks/shortcutKeyConstants";
import { UserRoleType } from "@api/graphql/generated/generated-types";

const CONFIGURATION_SECTIONS = {
  automation: "Automation",
  billing: "Pricing & Billing",
  operations: "Operations",
  data: "Data & Customization",
} as const;

export const NAV_ITEMS: NavItem[] = [
  {
    label: "Deliveries",
    href: "/deliveries/orders",
    icon: <PaperAirplaneIcon className="h-6 w-6" />,
    solidIcon: <PaperAirplaneSolidIcon className="h-6 w-6" />,
  },
  {
    label: "Office",
    href: "/office/customers",
    icon: <PresentationChartBarIcon className="h-6 w-6" />,
    solidIcon: <PresentationChartBarSolidIcon className="h-6 w-6" />,
    minimumUserRoleType: UserRoleType.Dispatcher,
  },
  {
    label: "Analytics",
    href: "/analytics/dashboards",
    icon: <ChartPieIcon className="h-6 w-6" />,
    solidIcon: <ChartPieSolidIcon className="h-6 w-6" />,
    requiredPermission: "analytics.view",
  },
  {
    label: "Team",
    href: "/team/drivers",
    icon: <UserGroupIcon className="h-6 w-6" />,
    solidIcon: <UserGroupSolidIcon className="h-6 w-6" />,
  },
  {
    label: "Configuration",
    href: "/configuration/pricingrules",
    icon: <Cog8ToothIcon className="h-6 w-6" />,
    solidIcon: <Cog8ToothSolidIcon className="h-6 w-6" />,
    popoverWidth: 440,
  },
];

export const SUBMENU_ITEMS: SUBMENU_ITEM[] = [
  {
    page: "deliveries",
    items: [
      {
        label: "Orders",
        href: "/deliveries/orders",
        minimumUserRoleType: UserRoleType.Csr,
        icon: <RectangleStackIcon className="h-6 w-6" />,
        shortcut: ORDERS_KEY,
      },
      {
        label: "Fleet",
        href: "/deliveries/fleet",
        minimumUserRoleType: UserRoleType.Csr,
        icon: <RocketLaunchIcon className="h-6 w-6" />,
        shortcut: FLEET_KEY,
      },
      {
        label: "Routes",
        href: "/routes/table",
        minimumUserRoleType: UserRoleType.Dispatcher,
        icon: <Route className="h-6 w-6" />,
      },
      {
        label: "Routes Map",
        href: "/routes/monitoring",
        minimumUserRoleType: UserRoleType.Dispatcher,
        icon: <MapIcon className="h-6 w-6" />,
      },
      {
        label: "Recurring",
        href: "/deliveries/recurring",
        minimumUserRoleType: UserRoleType.Csr,
        icon: <ArrowPathRoundedSquareIcon className="h-6 w-6" />,
      },
      {
        label: "Packages",
        href: "/deliveries/packages",
        minimumUserRoleType: UserRoleType.Csr,
        icon: <CubeIcon className="h-6 w-6" />,
        shortcut: PACKAGES_KEY,
      },
      {
        label: "Quotes",
        href: "/deliveries/quotes",
        minimumUserRoleType: UserRoleType.Dispatcher,
        icon: <BookText className="h-6 w-6" />,
        shortcut: QUOTES_KEY,
      },
      {
        label: "Schedules",
        href: "/deliveries/schedules",
        minimumUserRoleType: UserRoleType.Csr,
        icon: <CalendarDaysIcon className="h-6 w-6" />,
        shortcut: SCHEDULES_KEY,
      },
      {
        label: "Create Order",
        href: "/orders/create",
        minimumUserRoleType: UserRoleType.Csr,
        icon: <PlusIcon className="h-6 w-6" />,
        shortcut: CREATE_KEY,
      },
    ],
  },
  {
    page: "office",
    items: [
      {
        label: "Customers",
        href: "/office/customers",
        minimumUserRoleType: UserRoleType.Dispatcher,
        icon: <UsersIcon className="h-6 w-6" />,
        shortcut: CUSTOMER_KEY,
      },
      {
        label: "Sales",
        href: "/office/sales",
        minimumUserRoleType: UserRoleType.Administrator,
        requiredPermission: "salesReports.view",
        icon: <PresentationChartLineIcon className="h-6 w-6" />,
      },
      {
        label: "Driving",
        href: "/office/driving",
        minimumUserRoleType: UserRoleType.Administrator,
        icon: <ChartBarSquareIcon className="h-6 w-6" />,
      },
      {
        label: "Invoices",
        href: "/office/invoice",
        minimumUserRoleType: UserRoleType.Dispatcher,
        icon: <ReceiptPercentIcon className="h-6 w-6" />,
      },
      {
        label: "Pay",
        href: "/office/payroll",
        minimumUserRoleType: UserRoleType.Administrator,
        icon: <BanknotesIcon className="h-6 w-6" />,
      },
      {
        label: "Payments",
        href: "/office/payments",
        minimumUserRoleType: UserRoleType.Dispatcher,
        icon: <CreditCardIcon className="h-6 w-6" />,
      },
      {
        label: "Emails",
        href: "/office/emails",
        minimumUserRoleType: UserRoleType.Dispatcher,
        icon: <EnvelopeOpenIcon className="h-6 w-6" />,
      },
    ],
  },
  {
    page: "team",
    items: [
      {
        label: "Drivers",
        href: "/team/drivers",
        minimumUserRoleType: UserRoleType.Csr,
        icon: <IdentificationIcon className="h-6 w-6" />,
      },
      {
        label: "Users",
        href: "/team/users",
        minimumUserRoleType: UserRoleType.Csr,
        icon: <UserCircleIcon className="h-6 w-6" />,
      },
      {
        label: "Driver Documents",
        href: "/team/driver-documents",
        minimumUserRoleType: UserRoleType.Dispatcher,
        icon: <DocumentTextIcon className="h-6 w-6" />,
      },
    ],
  },
  {
    page: "configuration",
    items: [
      {
        label: "Agents",
        href: "/configuration/agents",
        section: CONFIGURATION_SECTIONS.automation,
        minimumUserRoleType: UserRoleType.Administrator,
        icon: <SparklesIcon className="h-6 w-6" />,
      },
      {
        label: "Order Automations",
        href: "/configuration/orderautomations",
        section: CONFIGURATION_SECTIONS.automation,
        minimumUserRoleType: UserRoleType.Administrator,
        requiredPermission: "orderAutomations.view",
        icon: <ArrowPathRoundedSquareIcon className="h-6 w-6" />,
      },
      {
        label: "Pricing",
        href: "/configuration/pricingrules",
        section: CONFIGURATION_SECTIONS.billing,
        minimumUserRoleType: UserRoleType.Csr,
        icon: <CalculatorIcon className="h-6 w-6" />,
      },
      {
        label: "Services",
        href: "/configuration/servicetypes",
        section: CONFIGURATION_SECTIONS.billing,
        minimumUserRoleType: UserRoleType.Csr,
        icon: <PuzzlePieceIcon className="h-6 w-6" />,
      },
      {
        label: "Zones",
        href: "/configuration/zones",
        section: CONFIGURATION_SECTIONS.billing,
        minimumUserRoleType: UserRoleType.Csr,
        icon: <GlobeAmericasIcon className="h-6 w-6" />,
      },
      {
        label: "Billable Items",
        href: "/configuration/billableitems",
        section: CONFIGURATION_SECTIONS.billing,
        minimumUserRoleType: UserRoleType.Dispatcher,
        requiredPermission: "billableItems.view",
        icon: <BanknotesIcon className="h-6 w-6" />,
        isNew: true,
      },
      {
        label: "Invoice Templates",
        href: "/configuration/invoice-templates",
        section: CONFIGURATION_SECTIONS.billing,
        minimumUserRoleType: UserRoleType.Csr,
        icon: <ReceiptPercentIcon className="h-6 w-6" />,
      },
      {
        label: "Compensation Deductions",
        href: "/configuration/payrolldeductions",
        section: CONFIGURATION_SECTIONS.billing,
        minimumUserRoleType: UserRoleType.Csr,
        icon: <BanknotesIcon className="h-6 w-6" />,
      },
      {
        label: "Vehicle Types",
        href: "/configuration/vehicletypes",
        section: CONFIGURATION_SECTIONS.operations,
        minimumUserRoleType: UserRoleType.Csr,
        icon: <TruckIcon className="h-6 w-6" />,
      },
      {
        label: "Package Types",
        href: "/configuration/packagetypes",
        section: CONFIGURATION_SECTIONS.operations,
        minimumUserRoleType: UserRoleType.Csr,
        icon: <CubeIcon className="h-6 w-6" />,
      },
      {
        label: "Scan Modes",
        href: "/configuration/scanmodes",
        section: CONFIGURATION_SECTIONS.operations,
        minimumUserRoleType: UserRoleType.Csr,
        icon: <QrCodeIcon className="h-6 w-6" />,
      },
      {
        label: "Driver sub tasks",
        href: "/configuration/driver-sub-tasks",
        section: CONFIGURATION_SECTIONS.operations,
        minimumUserRoleType: UserRoleType.Csr,
        icon: <QuestionMarkCircleIcon className="h-6 w-6" />,
      },
      {
        label: "Holiday Schedules",
        href: "/configuration/holiday-schedules",
        section: CONFIGURATION_SECTIONS.operations,
        minimumUserRoleType: UserRoleType.Csr,
        icon: <CalendarDaysIcon className="h-6 w-6" />,
      },
      {
        label: "Exception Reasons",
        href: "/configuration/exceptionreasons",
        section: CONFIGURATION_SECTIONS.operations,
        minimumUserRoleType: UserRoleType.Dispatcher,
        requiredPermission: "exceptionReasons.view",
        icon: <ExclamationTriangleIcon className="h-6 w-6" />,
        isNew: true,
      },
      {
        label: "Order Grouping",
        href: "/configuration/ordergrouping",
        section: CONFIGURATION_SECTIONS.operations,
        minimumUserRoleType: UserRoleType.Dispatcher,
        requiredPermission: "orderGroupBuilders.view",
        icon: <Group className="h-6 w-6" />,
        isNew: true,
      },
      {
        label: "Address Book",
        href: "/configuration/addressbook",
        section: CONFIGURATION_SECTIONS.data,
        minimumUserRoleType: UserRoleType.Csr,
        icon: <BookOpenIcon className="h-6 w-6" />,
      },
      {
        label: "Custom Fields",
        href: "/configuration/customfields",
        section: CONFIGURATION_SECTIONS.data,
        minimumUserRoleType: UserRoleType.Csr,
        icon: <SwatchIcon className="h-6 w-6" />,
      },
      {
        label: "Labels",
        href: "/configuration/labels",
        section: CONFIGURATION_SECTIONS.data,
        minimumUserRoleType: UserRoleType.Csr,
        icon: <TagIcon className="h-6 w-6" />,
      },
      {
        label: "Email Templates",
        href: "/configuration/email-templates",
        section: CONFIGURATION_SECTIONS.data,
        minimumUserRoleType: UserRoleType.Csr,
        icon: <EnvelopeIcon className="h-6 w-6" />,
      },
    ],
  },
];
